import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardHeader } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { Loading } from '../../components/common/Loading';
import { Avatar } from '../../components/common/Avatar';
import { getUserById, updateUserRole } from '../../api/users';
import { createInvite } from '../../api/invites';
import { formatDate, formatDateTime } from '../../utils/formatters';
import { ROUTES } from '../../utils/constants';
import type { User, UserProfile } from '../../types/user';

interface UserDetailProps {
  tenant: string;
}

interface UserWithTimestamps extends User {
  createdAt: number;
  lastLoginAt: number | null;
}

export function UserDetail({ tenant }: UserDetailProps) {
  const { id } = useParams<{ id: string }>();
  const [user, setUser] = useState<UserWithTimestamps | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isUpdatingRole, setIsUpdatingRole] = useState(false);
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  const [isCreatingInvite, setIsCreatingInvite] = useState(false);
  const [inviteCopied, setInviteCopied] = useState(false);

  useEffect(() => {
    const loadUser = async () => {
      if (!id) return;

      try {
        const data = await getUserById(tenant, id);
        setUser(data.user);
        setProfile(data.profile);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load user');
      } finally {
        setIsLoading(false);
      }
    };

    loadUser();
  }, [tenant, id]);

  const handleRoleChange = async (newRole: 'user' | 'coach' | 'admin') => {
    if (!id || !user) return;

    setIsUpdatingRole(true);
    try {
      const data = await updateUserRole(tenant, id, newRole);
      setUser({ ...user, role: data.user.role });
    } catch (err) {
      console.error('Failed to update role:', err);
    } finally {
      setIsUpdatingRole(false);
    }
  };

  const handleCreateInvite = async () => {
    setIsCreatingInvite(true);
    try {
      const data = await createInvite(tenant, { maxUses: 1, expiresInDays: 7 });
      setInviteUrl(data.inviteUrl);
    } catch (err) {
      console.error('Failed to create invite:', err);
    } finally {
      setIsCreatingInvite(false);
    }
  };

  const handleCopyInvite = async () => {
    if (!inviteUrl) return;
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setInviteCopied(true);
      setTimeout(() => setInviteCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  if (isLoading) {
    return <Loading message="Loading user..." />;
  }

  if (error || !user) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">{error || 'User not found'}</p>
        <Link to={ROUTES.coach.users(tenant)} className="mt-4 inline-block">
          <Button variant="outline">Back to Users</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Link to={ROUTES.coach.users(tenant)}>
          <Button variant="ghost" size="sm">
            <svg
              className="h-4 w-4 mr-1"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Back
          </Button>
        </Link>
        <h1 className="text-2xl font-bold text-gray-900">User Details</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* User Info Card */}
        <Card className="lg:col-span-1">
          <div className="text-center">
            <Avatar src={user.avatarUrl} name={user.name} size="xl" className="mx-auto" />
            <h2 className="mt-4 text-xl font-semibold text-gray-900">{user.name}</h2>
            <p className="text-gray-500">{user.email}</p>
            <span
              className={`mt-2 inline-flex px-3 py-1 text-sm font-medium rounded-full ${
                user.role === 'admin'
                  ? 'bg-purple-100 text-purple-800'
                  : user.role === 'coach'
                  ? 'bg-blue-100 text-blue-800'
                  : 'bg-gray-100 text-gray-800'
              }`}
            >
              {user.role}
            </span>
          </div>

          <div className="mt-6 pt-6 border-t space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Joined</span>
              <span className="text-gray-900">{formatDate(user.createdAt)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Last Active</span>
              <span className="text-gray-900">
                {user.lastLoginAt ? formatDateTime(user.lastLoginAt) : 'Never'}
              </span>
            </div>
          </div>
        </Card>

        {/* Profile & Actions */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader title="Profile Information" />
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Bio</label>
                <p className="mt-1 text-gray-900">{profile?.bio || 'No bio provided'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Timezone</label>
                <p className="mt-1 text-gray-900">{profile?.timezone || 'Not set'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Onboarding</label>
                <p className="mt-1 text-gray-900">
                  {profile?.onboardingCompleted ? 'Completed' : 'Not completed'}
                </p>
              </div>
            </div>
          </Card>

          <Card>
            <CardHeader
              title="Role Management"
              description="Change the user's role and permissions"
            />
            <div className="flex flex-wrap gap-2">
              {(['user', 'coach', 'admin'] as const).map((role) => (
                <Button
                  key={role}
                  variant={user.role === role ? 'primary' : 'outline'}
                  size="sm"
                  onClick={() => handleRoleChange(role)}
                  isLoading={isUpdatingRole}
                  disabled={user.role === role}
                >
                  {role.charAt(0).toUpperCase() + role.slice(1)}
                </Button>
              ))}
            </div>
          </Card>

          <Card>
            <CardHeader
              title="Invite User"
              description="Generate an invite link to share with new users"
            />
            {inviteUrl ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    readOnly
                    value={inviteUrl}
                    className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md bg-gray-50"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCopyInvite}
                  >
                    {inviteCopied ? 'Copied!' : 'Copy'}
                  </Button>
                </div>
                <p className="text-xs text-gray-500">
                  This link expires in 7 days and can be used once.
                </p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setInviteUrl(null)}
                >
                  Create new invite
                </Button>
              </div>
            ) : (
              <Button
                variant="primary"
                onClick={handleCreateInvite}
                isLoading={isCreatingInvite}
              >
                Generate Invite Link
              </Button>
            )}
          </Card>

          <Card>
            <CardHeader
              title="User's Home Page"
              description="View what this user sees on their home page"
            />
            <div className="text-center py-8 text-gray-500 border-2 border-dashed rounded-lg">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                />
              </svg>
              <p className="mt-2">View as user feature coming soon</p>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
